import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// GET actions for a membership
export const getMembershipActions = (token, membershipId, { status, includeCompleted } = {}) => {
  const params = {};
  if (status) params.status = status;
  if (includeCompleted !== undefined) params.includeCompleted = includeCompleted;

  return axios
    .get(`${BASE_URL}/membership-actions/${membershipId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// GET my overdue actions
export const getMyOverdueActions = (token) => {
  return axios
    .get(`${BASE_URL}/membership-actions/overdue/my`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// GET all overdue actions (admin)
export const getAllOverdueActions = (token) => {
  return axios
    .get(`${BASE_URL}/membership-actions/overdue/all`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// GET tasks across every assignee (manage_membership_actions).
// scope: "all" | "unassigned". Resolves to { success, data, truncated, limit }:
// the list is capped, and `truncated` says there were more than it returned.
export const listTasks = (token, { scope = "all", includeCompleted } = {}) => {
  const params = { scope };
  if (includeCompleted !== undefined) params.includeCompleted = includeCompleted;

  return axios
    .get(`${BASE_URL}/membership-actions/list`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// POST create action
export const createAction = (token, actionData) => {
  return axios
    .post(`${BASE_URL}/membership-actions`, actionData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// PUT update action
export const updateAction = (token, id, actionData) => {
  return axios
    .put(`${BASE_URL}/membership-actions/${id}`, actionData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// PUT complete action
export const completeAction = (token, id) => {
  return axios
    .put(`${BASE_URL}/membership-actions/${id}/complete`, {}, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// POST bulk update: complete, cancel, or snooze several actions at once.
// operation: "complete" | "cancel" | "snooze"; days is required for snooze (1-90).
// Resolves to { success, data: { updated, skipped, notFound, failed } }.
export const bulkUpdateActions = (token, ids, operation, days) => {
  const body = { ids, operation };
  if (operation === "snooze") body.days = days;
  return axios
    .post(`${BASE_URL}/membership-actions/bulk`, body, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// ---- Task detail: subtasks, comments, attachments --------------------------
// All go through the backend with the staff token. Attachments upload as
// multipart to the backend, never straight to Supabase storage: the browser has
// no Supabase session, so it cannot write to the private bucket itself.

const authHeaders = (token, json = true) => ({
  ...(json ? { "Content-Type": "application/json" } : {}),
  authorization: `Bearer ${token}`,
});

const unwrap = (promise) =>
  promise.then((res) => (res ? res?.data : undefined)).catch((err) => err?.response?.data);

// GET one task (manage_membership_actions), with the same fields as listTasks.
// For links to a task the reader does not hold, e.g. a comment notification.
export const getTask = (token, taskId) =>
  unwrap(axios.get(`${BASE_URL}/membership-actions/task/${taskId}`, { headers: authHeaders(token) }));

// GET subtasks of a task
export const getSubtasks = (token, taskId) =>
  unwrap(axios.get(`${BASE_URL}/membership-actions/${taskId}/subtasks`, { headers: authHeaders(token) }));

// GET comments on a task (oldest first)
export const getTaskComments = (token, taskId) =>
  unwrap(axios.get(`${BASE_URL}/membership-actions/${taskId}/comments`, { headers: authHeaders(token) }));

// POST a comment
export const addTaskComment = (token, taskId, body) =>
  unwrap(axios.post(`${BASE_URL}/membership-actions/${taskId}/comments`, { body }, { headers: authHeaders(token) }));

// DELETE your own comment
export const deleteTaskComment = (token, taskId, commentId) =>
  unwrap(axios.delete(`${BASE_URL}/membership-actions/${taskId}/comments/${commentId}`, { headers: authHeaders(token) }));

// GET attachments (each with a short-lived signedUrl)
export const getTaskAttachments = (token, taskId) =>
  unwrap(axios.get(`${BASE_URL}/membership-actions/${taskId}/attachments`, { headers: authHeaders(token) }));

// POST one file as multipart field "file"
export const uploadTaskAttachment = (token, taskId, file) => {
  const form = new FormData();
  form.append("file", file);
  return unwrap(
    axios.post(`${BASE_URL}/membership-actions/${taskId}/attachments`, form, {
      headers: authHeaders(token, false),
    })
  );
};

// DELETE an attachment (file and record)
export const deleteTaskAttachment = (token, taskId, attachmentId) =>
  unwrap(axios.delete(`${BASE_URL}/membership-actions/${taskId}/attachments/${attachmentId}`, { headers: authHeaders(token) }));

// PUT assign action
export const assignAction = (token, id, assignedTo) => {
  return axios
    .put(`${BASE_URL}/membership-actions/${id}/assign`, { assigned_to: assignedTo }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// DELETE action
export const deleteAction = (token, id) => {
  return axios
    .delete(`${BASE_URL}/membership-actions/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};
